import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { PhoneField } from "../../components/PhoneField/PhoneField";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { extractErrorMessage } from "../../utils/extractErrorMessage";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

export default function BiographySimple() {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const timeZone = getUserTimeZone();
    localStorage.setItem("user_status", "worker");
    localStorage.setItem("onboarding_code", "234");
    localStorage.setItem("worker_biography_start", new Date().toISOString());
    localStorage.setItem("worker_biography_timezone", timeZone);
  }, []);

  const handlePhoneChange = (value, isValid) => {
    setPhone(value);
    if (isValid) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.phone;
        return next;
      });
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!fullName.trim() || fullName.trim().length < 2) {
      nextErrors.fullName = "Full name is required (min 2 characters).";
    }

    const parsed = parsePhoneNumberFromString(phone, selectedCountry);
    if (!parsed || !parsed.isValid()) {
      nextErrors.phone = "Please enter a valid phone number.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      toast.error("Please fix the highlighted errors.");
      return false;
    }

    return true;
  };

  const getValidToken = async () => {
    let token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      "";

    token = String(token || "").trim();
    if (!token) return null;

    const verifyRes = await fetch(`${BASE_URL}/auth/token/verify/`, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify({ token }),
    });

    if (verifyRes.ok) return token;

    const refresh =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken") ||
      "";

    if (!String(refresh).trim()) return null;

    const refreshRes = await fetch(`${BASE_URL}/auth/token/refresh/`, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify({ refresh: String(refresh).trim() }),
    });

    const data = await refreshRes.json().catch(() => ({}));

    if (refreshRes.ok && data.access) {
      if (
        localStorage.getItem("accessToken") ||
        localStorage.getItem("refreshToken")
      ) {
        localStorage.setItem("accessToken", data.access);
      } else {
        sessionStorage.setItem("accessToken", data.access);
      }

      return data.access;
    }

    return null;
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const token = await getValidToken();

      if (!token) {
        toast.error("Session expired. Please log in again.");
        navigate("/sign-in", { replace: true });
        return;
      }

      const parsed = parsePhoneNumberFromString(phone, selectedCountry);
      const normalizedPhone = parsed?.number || phone;
      const timeZone = getUserTimeZone();

      const body = {
        full_name: fullName.trim(),
        phone_number: normalizedPhone,
        timeZone: timeZone,
        country: selectedCountry,
      };

      const res = await fetch(`${BASE_URL}/auth/userinfo/`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(extractErrorMessage(data, "Failed to save data."));
      }

      localStorage.setItem("worker_biography_saved", new Date().toISOString());
      localStorage.setItem("worker_name", fullName.trim());
      localStorage.setItem("worker_phone", normalizedPhone);
      localStorage.setItem("worker_country", selectedCountry);
      localStorage.setItem("user_status", "worker");
      localStorage.setItem("onboarding_code", "236");

      sessionStorage.setItem("onboarding_force_route", "/working-days");
      sessionStorage.setItem("onboarding_force_status", "worker");

      toast.success("Information saved successfully!");

      navigate("/working-days", {
        replace: true,
        state: {
          timeZone,
          fromWorkerBiography: true,
        },
      });
    } catch (err) {
      console.error("Worker biography save error:", err);
      toast.error(err.message || "Failed to save information.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Enter" && fullName.trim() && phone.trim() && !loading) {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [fullName, phone, loading]);

  return (
    <motion.div
      className="biography"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <main className="biography-main">
        <motion.div
          className="biography-card biography-card-worker"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="biography-header">
            <div className="biography-text">
              <h2>{t("auth.biographyHeader")}</h2>
              <p>{t("auth.biographySubheader")}</p>
            </div>
          </div>

          <div className="biography-content biography-content-worker">
            <div className="biography-form-group">
              <label>
                {t("auth.fullNameLabel")} <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder={t("auth.fullNamePlaceholder")}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={errors.fullName ? "invalid" : ""}
                autoFocus
                disabled={loading}
              />
              {errors.fullName && (
                <span className="input-error-text">{errors.fullName}</span>
              )}
            </div>

            <PhoneField
              phone={phone}
              error={errors.phone}
              label={t("phoneNumber")}
              required
              disabled={loading}
              placeholder={t("settings.enterPhoneNumber")}
              onPhoneChange={handlePhoneChange}
              onCountryChange={(c, code) => {
                setSelectedCountry(c);
              }}
            />
          </div>

          <div className="biography-btn-footer">
            <motion.button
              className="biography-next-btn biography-next-btn-worker"
              onClick={handleNext}
              disabled={loading || !fullName.trim() || !phone.trim()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? t("auth.saving") : t("auth.next")}
            </motion.button>

            <span className="biography-step">{t("step")} 1/2</span>
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
}
